import { Card, Col, InputGroup, Nav, Row } from "react-bootstrap";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import styled from "styled-components";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useState } from "react";
import Loading from "./Loading";
import DatepickerRange from "../molecules/DatepickerRange";
import moment from "moment/moment";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";

const Wrap = styled.div`
    .label {
        display: flex;
        align-items: center;
        gap: 0 5px;
        font-size: 12px;
        white-space: nowrap;
        
        .value {
            font-size: 14px;
            font-weight: bold;
        }

        .legend-indicator {
            width: 5px;
            height: 5px;
            border-radius: 100%;
            background-color: #bdc5d1;
        }
    }

    .datepicker-group {
        .input-group {
            flex-wrap: nowrap;

            .form-control {
                border-top-left-radius: 0px;
                border-bottom-left-radius: 0px;
            }
        }
    }
`;
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ChartDataLabels);
export default function CompanyDashboard({children}) {
    const location = useLocation();
    const {companyId, branchId} = useParams();

    // 등록 현황
    const [registrationStatus, setRegistrationStatus] = useState({
        newReport: 0,
        newUser: 0,
        revisitUser: 0,
    });

    // 성별
    const [genderData, setGenderData] = useState({
        f: 0,
        m: 0,
        confidential: 0,
        etc: 0,
        total: 0,
    })

    // 연령 분포
    const [ageDistributionData, setAgeDistributionData] = useState({
        labels: [],
        datasets: [
            {
                data: [],
                backgroundColor: ["rgba(55,125,255,1)", "rgba(55,125,255,0.5)", "rgba(55,125,255,0.25)", "rgba(0,201,167,1)", "rgba(0,201,167,0.5)", "rgba(0,201,167,0.25)", "rgba(189, 197, 209, 1)"],
            },
        ],
    })

    // 피부 문제 분포
    const [skinDistributionData, setSkinDistributionData] = useState({
        labels: ["모공", "블랙헤드", "색소스팟", "주름", "여드름", "홍조", "다크서클", "광채", "눈밑지방", "안검하수"],
        datasets: [
            {
                data: [],
                borderWidth: 1,
                backgroundColor: "#377dff",
                hoverBackgroundColor: "#377dff",
                borderColor: "#377dff",
                barPercentage: 0.5,
                barThickness: 15,
                maxBarThickness: 15,
            },
        ],
    });

    // 미용 치료 경험 
    const [experienceData, setExperienceData] = useState({
        labels: ["있음", "없음", "보안", "알수 없음"],
        datasets: [
            {
                data: [],
                backgroundColor: ["rgba(55,125,255,1)", "rgba(55,125,255,0.5)", "rgba(55,125,255,0.25)", "rgba(0,201,167,1)"],
            },
        ],
    });

    // 개선 희망 문제
    const [targetData, setTargetData] = useState({
        labels: ["모공", "블랙헤드", "색소스팟", "주름", "여드름", "홍조", "다크서클", "광채", "눈밑지방", "안검하수"],
        datasets: [
            {
                data: [],
                borderWidth: 1,
                backgroundColor: "#377dff",
                hoverBackgroundColor: "#377dff",
                borderColor: "#377dff",
                barPercentage: 0.5,
                barThickness: 15,
                maxBarThickness: 15,
            },
        ],
    });
    
    // 결혼 여부
    const [maritalData, setMaritalData] = useState({
        labels: ["기혼", "미혼", "보안", "알수 없음"],
        datasets: [
            {
                data: [],
                backgroundColor: ["rgba(55,125,255,1)", "rgba(55,125,255,0.5)", "rgba(55,125,255,0.25)", "rgba(0,201,167,1)"],
            },
        ],
    });

    const [startDate, setStartDate] = useState(moment().add(-7, "d").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    
    const {data, isError, isLoading, isRefetching, refetch, error} = useQuery({
        queryKey: ['dashboard'],
        queryFn: () => {
            return axios("/dashboard", {
                method: "POST",
                data: {
                    startDate: startDate,
                    endDate: endDate,
                    companyId: companyId,
                    branchId: branchId == 0 ? "" : branchId
                }
            }).then((result) => {
                if (result.data.code != "0") {
                    throw new Error(result);
                }

                return result.data;
            })
        },
        enabled: false,
    });

    useEffect(() => {
        if (data) {
            const result = data.data;
            // 등록 현황
            setRegistrationStatus({
                newReport: result.newReport,
                newUser: result.newUser,
                revisitUser: result.revisitUser,
            });
            
            // 성별
            setGenderData({
                f: result.gender.f,
                m: result.gender.m,
                confidential: result.gender.confidential,
                etc: result.gender.etc,
                total: result.gender.f + result.gender.m + result.gender.confidential + result.gender.etc
            });

            // 연령 분포
            const ageDistributionDataTemp = [];
            for (const key in result.age) {
                ageDistributionDataTemp.push(result.age[key]);
            }
            setAgeDistributionData({
                ...ageDistributionData,
                labels: ["10대 이하", "20대", "30대", "40대", "50대", "60대 이상", "알 수 없음"],
                datasets: [{
                    ...ageDistributionData.datasets[0],
                    data: ageDistributionDataTemp
                }]
            });

            // 피부 문제 분포
            // 순서 ["모공", "블랙헤드", "색소스팟", "주름", "여드름", "홍조", "다크서클", "광채", "눈밑지방", "안검하수"],
            const skinDistributionTemp = [
                result.skinTrouble.pore,
                result.skinTrouble.blackhead,
                result.skinTrouble.speckle,
                result.skinTrouble.wrinkle,
                result.skinTrouble.acne,
                result.skinTrouble.sensitive,
                result.skinTrouble.blackRimOfEye,
                result.skinTrouble.skinGlow,
                result.skinTrouble.eyeBags,
                result.skinTrouble.eyeSagging,
            ];
            setSkinDistributionData({
                ...skinDistributionData,
                datasets: [{
                    ...skinDistributionData.datasets[0],
                    data: skinDistributionTemp
                }]
            });

            // 미용 치료 경험 / 알러지 유무
            const experienceDataTemp = [
                result.hasBeauty["1"],
                result.hasBeauty["0"],
                result.hasBeauty["2"],
                result.hasBeauty["-1"],
            ];
            setExperienceData({
                ...experienceData,
                datasets: [{
                    ...experienceData.datasets[0],
                    data: experienceDataTemp
                }]
            });

            // 개선 희망 문제
            const targetTemp = [
                result.target.pore,
                result.target.blackhead,
                result.target.speckle,
                result.target.wrinkle,
                result.target.acne,
                result.target.sensitive,
                result.target.blackRimOfEye,
                result.target.skinGlow,
                result.target.eyeBags,
                result.target.eyeSagging,
            ];
            // 순서 ["모공", "블랙헤드", "색소스팟", "주름", "여드름", "홍조", "다크서클", "광채", "눈밑지방", "안검하수"],
            setTargetData({
                ...targetData,
                datasets: [{
                    ...targetData.datasets[0],
                    data: targetTemp
                }]
            });

            // 결혼 여부
            const maritalDataTemp = [
                result.maritalStatus["1"],
                result.maritalStatus["0"],
                result.maritalStatus["2"],
                result.maritalStatus["-1"],
            ];
            setMaritalData({
                ...maritalData,
                datasets: [{
                    ...maritalData.datasets[0],
                    data: maritalDataTemp
                }]
            });
        }
    },[data]);

    // 날짜 검색
    useEffect(() => {
        refetch();
    },[startDate, endDate, companyId, branchId]);

    useEffect(() => {
        refetch();
    },[location])

    if (isLoading || isRefetching) {
        return <Loading/>
    }

    if (isError) {
        if (error.response) {
            return <div>{error.response.data.msg.ko}</div>
        }
    }

    return (
        data &&
        <Wrap className="px-3">
            <div className="datepicker-group d-flex justify-content-end">
                <div style={{ position: 'relative' }}>
                    <DatepickerRange startDate={startDate} endDate={endDate} 
                        onChange={(date) => {
                            setStartDate(date[0]);
                            setEndDate(date[1]);
                        }} 
                    />
                </div>
            </div>
            <>
            <Row className="mt-3">
                {/* (S) 등록 현황 */}
                <Col xl={5} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                            등록 현황
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column">
                            <Row>
                                <Col xs={4} className="border-end">
                                    <div className="text-center">
                                        <div className="fs-2 fw-bold">{registrationStatus.newReport}</div>
                                        <div className="small text-secondary mt-3">신규 리포트</div>
                                    </div>
                                </Col>

                                <Col>
                                    <div className="d-flex justify-content-around">
                                        <div className="text-center">
                                            <div className="fs-2 fw-bold">{registrationStatus.newUser}</div>
                                            <div className="small text-secondary mt-3">신규 고객 수</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="fs-2 fw-bold">{registrationStatus.revisitUser}</div>
                                            <div className="small text-secondary mt-3">재방문 고객 수</div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                {/* (E) 등록 현황 */}

                {/* (S) 성별 */}
                <Col xl={7} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                            성별
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column">
                            <div className="progress">
                                <div className="progress-bar" style={{ width: genderData.f / genderData.total * 100 + "%" }}></div>
                                <div className="progress-bar bg-info" style={{ width: genderData.m / genderData.total * 100 + "%" }}></div>
                                <div className="progress-bar bg-black opacity-50" style={{ width: genderData.confidential / genderData.total * 100 + "%" }}></div>
                                <div className="progress-bar bg-secondary" style={{ width: genderData.etc / genderData.total * 100 + "%" }}></div>
                            </div>

                            <div className="d-flex flex-wrap justify-content-center gap-4 mt-3">
                                <div className="label">
                                    <div className="bg-primary legend-indicator"></div>
                                    여성
                                    <div className="value ms-1">{genderData.f}명</div>
                                </div>
                                <div className="label">
                                    <div className="bg-info legend-indicator"></div>
                                    남성
                                    <div className="value ms-1">{genderData.m}명</div>
                                </div>
                                <div className="label">
                                    <div className="bg-black legend-indicator"></div>
                                    보안
                                    <div className="value ms-1">{genderData.confidential}명</div>
                                </div>
                                <div className="label">
                                    <div className="legend-indicator"></div>
                                    알 수 없음
                                    <div className="value ms-1">{genderData.etc}명</div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                {/* (E) 성별 */}
            </Row>

            <Row className="mt-4">
                {/* 연령 분포 */}
                <Col xl={5} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                            연령 분포
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex justify-content-evenly align-items-center mt-3 flex-grow-1">
                                <Doughnut
                                    width={200}
                                    height={200}
                                    data={ageDistributionData}
                                    options={{
                                        responsive: false,
                                        cutout: 70,
                                        plugins: {
                                            datalabels: {
                                                display: false,
                                            },
                                            legend: {
                                                display: false,
                                            },
                                            tooltip: {
                                                
                                                callbacks: {
                                                    label: (cont) => {
                                                        return cont.parsed + '명';
                                                    }
                                                }
                                            }
                                        },
                                    }}
                                />

                                <div className="d-flex flex-column justify-content-center">
                                    {ageDistributionData.datasets[0].data.map((val, idx) => {
                                        return (
                                            <div className={idx != 0 ? "label mt-2" : "label"} key={idx}>
                                                <div className="legend-indicator" style={{ background: ageDistributionData.datasets[0].backgroundColor[idx] }}></div>
                                                <div style={{ width: "50px" }}>{ageDistributionData.labels[idx]}</div>
                                                <span className="fw-bold ms-2 value">{val}명</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                {/* 연령 분포 */}

                {/* 피부 문제 분포 */}
                <Col xl={7} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                                피부 문제 분포
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex justify-content-center">
                            {
                                skinDistributionData.datasets[0].data &&
                                <Bar
                                    data={skinDistributionData}
                                    plugins={[ChartDataLabels]}
                                    options={{
                                        responsive: true,
                                        plugins: {
                                            datalabels: {
                                                font: {
                                                    family: "'Pretendard', sans-serif",
                                                },
                                                anchor: "end",
                                                align: "end",  
                                                color: "#000",
                                            },  
                                            legend: {
                                                display: false,
                                            },
                                            tooltip: {
                                                intersect: false,
                                            },
                                        },
                                        scales: {
                                            x: {
                                                grid: {
                                                    display: false,
                                                },
                                                ticks: {
                                                    stepSize: 10,
                                                    font: {
                                                        family: "'Pretendard', sans-serif",
                                                    },
                                                    color: "#97a4af",
                                                },
                                            },
                                            y: {
                                                ticks: {
                                                    stepSize: 20,
                                                    font: {
                                                        family: "'Pretendard', sans-serif",
                                                    },
                                                    borderColor: "#97a4af",
                                                    color: "#97a4af",
                                                    callback: function(value, idx) {
                                                        if (value <= 100) {
                                                            return value + '%'; // y축 값 뒤에 '%' 기호 추가
                                                        }
                                                    },
                                                },
                                                grid: {
                                                    borderColor: "#97a4af",
                                                    tickWidth: 0,
                                                },
                                                min: 0,
                                                max: 110,
                                            }
                                        },
                                    }}
                                />
                            }
                        </Card.Body>
                    </Card>
                </Col>
                {/* 피부 문제 분포 */}
            </Row>

            <Row className="mt-4">
                {/* 경험 및 체질 */}
                <Col xl={5} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                                경험 및 체질
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column">
                            <Nav className="nav nav-segment w-fit-content" defaultActiveKey="exprience">
                                <Nav.Item>
                                    <Nav.Link eventKey="exprience" onClick={() => {
                                        const experienceDataTemp = [
                                            data.data.hasBeauty["1"],
                                            data.data.hasBeauty["0"],
                                            data.data.hasBeauty["2"],
                                            data.data.hasBeauty["-1"],
                                        ];
                                        setExperienceData({
                                            ...experienceData,
                                            datasets: [{
                                                ...experienceData.datasets[0],
                                                data: experienceDataTemp
                                            }]
                                        });
                                    }}>미용 치료 경험</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="allergy" onClick={() => {
                                        const experienceDataTemp = [
                                            data.data.isIrritability["1"],
                                            data.data.isIrritability["0"],
                                            data.data.isIrritability["2"],
                                            data.data.isIrritability["-1"],
                                        ];
                                        setExperienceData({
                                            ...experienceData,
                                            datasets: [{
                                                ...experienceData.datasets[0],
                                                data: experienceDataTemp
                                            }]
                                        });
                                    }}>알러지 체질</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <div className="d-flex justify-content-evenly align-items-center mt-3 flex-grow-1">
                                <Doughnut
                                    width={200}
                                    height={200}
                                    data={experienceData}
                                    options={{
                                        responsive: false,
                                        cutout: 70,
                                        plugins: {
                                            datalabels: {
                                                display: false,
                                            },
                                            legend: {
                                                display: false,
                                            },
                                            tooltip: {
                                                
                                                callbacks: {
                                                    label: (cont) => {
                                                        return cont.parsed + '명';
                                                    }
                                                }
                                            }
                                        },
                                    }}
                                />

                                <div className="d-flex flex-column justify-content-center">
                                    {experienceData.datasets[0].data.map((val, idx) => {
                                        return (
                                            <div className={idx != 0 ? "label mt-2" : "label"} key={idx}>
                                                <div className="legend-indicator" style={{ background: experienceData.datasets[0].backgroundColor[idx] }}></div>
                                                <div style={{ width: "50px" }}>{experienceData.labels[idx]}</div>
                                                <span className="fw-bold ms-2 value">{val}명</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                {/* 경험 및 체질 */}

                {/* 개선 희망 문제 */}
                <Col xl={7} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                                개선 희망 문제
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex justify-content-center">
                            <Bar
                                data={targetData}
                                plugins={[ChartDataLabels]}
                                options={{
                                    responsive: true,
                                    plugins: {
                                        datalabels: {
                                            font: {
                                                family: "'Pretendard', sans-serif",
                                            },
                                            anchor: "end",
                                            align: "end",  
                                            color: "#000"
                                        },  
                                        legend: {
                                            display: false,
                                        },
                                        tooltip: {
                                            intersect: false,
                                        },
                                    },
                                    scales: {
                                        x: {
                                            grid: {
                                                display: false,
                                            },
                                            ticks: {
                                                stepSize: 10,
                                                font: {
                                                    family: "'Pretendard', sans-serif",
                                                },
                                                color: "#97a4af",
                                            },
                                        },
                                        y: {
                                            ticks: {
                                                stepSize: 20,
                                                font: {
                                                    family: "'Pretendard', sans-serif",
                                                },
                                                borderColor: "#97a4af",
                                                color: "#97a4af",
                                                callback: function(value) {
                                                    if (value <= 100) {
                                                        return value + '%'; // y축 값 뒤에 '%' 기호 추가
                                                    }
                                                },
                                            },
                                            grid: {
                                                borderColor: "#97a4af",
                                                tickWidth: 0,
                                            },
                                            min: 0,
                                            max: 110,
                                        }
                                    },
                                }}
                            />
                        </Card.Body>
                    </Card>
                </Col>
                {/* 개선 희망 문제 */}
            </Row>

            <Row className="mt-4">
                {/* 개인 정보 */}
                <Col xl={5} xs={6}>
                    <Card className="h-100">
                        <Card.Header>
                            <Card.Title>
                                개인 정보
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column">
                            <Nav className="nav nav-segment w-fit-content" defaultActiveKey="marital-status">
                                <Nav.Item>
                                    <Nav.Link eventKey="marital-status" onClick={() => {
                                        const maritalDataTemp = [
                                            data.data.maritalStatus["1"],
                                            data.data.maritalStatus["0"],
                                            data.data.maritalStatus["2"],
                                            data.data.maritalStatus["-1"],
                                        ];
                                        setMaritalData({
                                            ...maritalData,
                                            labels: ["기혼", "미혼", "보안", "알 수 없음"],
                                            datasets: [{
                                                ...maritalData.datasets[0],
                                                data: maritalDataTemp
                                            }]
                                        });
                                    }}>결혼 여부</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="has-baby" onClick={() => {
                                        const hasBabyDataTemp = [
                                            data.data.hasBaby["1"],
                                            data.data.hasBaby["0"],
                                            data.data.hasBaby["2"],
                                            data.data.hasBaby["-1"],
                                        ];
                                        setMaritalData({
                                            ...maritalData,
                                            labels: ["있음", "없음", "보안", "알 수 없음"],
                                            datasets: [{
                                                ...maritalData.datasets[0],
                                                data: hasBabyDataTemp
                                            }]
                                        });
                                    }}>어린 자녀 유무</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <div className="d-flex justify-content-evenly align-items-center mt-3 flex-grow-1">
                                <Doughnut
                                    width={200}
                                    height={200}
                                    data={maritalData}
                                    options={{
                                        responsive: false,
                                        cutout: 70,
                                        plugins: {
                                            datalabels: {
                                                display: false,
                                            },
                                            legend: {
                                                display: false,
                                            },
                                            tooltip: {
                                                
                                                callbacks: {
                                                    label: (cont) => {
                                                        return cont.parsed + '명';
                                                    }
                                                }
                                            }
                                        },
                                    }}
                                />

                                <div className="d-flex flex-column justify-content-center">
                                    {maritalData.datasets[0].data.map((val, idx) => {
                                        return (
                                            <div className={idx != 0 ? "label mt-2" : "label"} key={idx}>
                                                <div className="legend-indicator" style={{ background: maritalData.datasets[0].backgroundColor[idx] }}></div>
                                                <div style={{ width: "50px" }}>{maritalData.labels[idx]}</div>
                                                <span className="fw-bold ms-2 value">{val}명</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                {/* 개인 정보 */}
            </Row>
            </>
        </Wrap>
    );
}